/**
 * E2E Auth Injection - Security Seam
 *
 * This module provides a controlled seam for E2E test authentication.
 * It is ONLY enabled when:
 *   1. Build mode is 'e2e' (not production)
 *   2. VITE_E2E_AUTH_INJECTION is explicitly set to 'true'
 *
 * The MODE check is the primary authority. Even if the flag is accidentally
 * set in production mode, the seam remains closed.
 */

/// <reference types="vite/client" />

import type { AuthState } from '@/store/authStore';
import { useAuthStore } from '@/store/authStore';

export const E2E_SESSION_KEY = 'e2e-session-token';

/**
 * Internal E2E auth state injected via Playwright before page load.
 * Set via window.__INDEEP_E2E_AUTH_STATE__ in e2eAuthInjectionBrowserSmoke.test.ts
 */
interface E2EAuthUser {
  id: string;
  email: string;
  registration_kind: 'friend' | 'sponsor' | 'unclassified';
  profile_status: 'inactive' | 'active';
  has_sponsor_access?: boolean;
  sponsor_tier?: 'none' | 'sponsor';
  is_admin?: boolean;
}

interface WindowWithE2EAuth extends Window {
  __INDEEP_E2E_AUTH_STATE__?: Readonly<{
    user: E2EAuthUser;
    authenticated: boolean;
  }>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: WindowWithE2EAuth;

/**
 * Pure flag-classification function for testability.
 *
 * The flag is ONLY enabled when its value is exactly the string "true".
 * All other values (false, undefined, empty, etc.) result in disabled.
 *
 * Security rationale: The flag name ("true") must be explicitly set.
 * Accidential values like "1", "yes", "enabled" are all treated as disabled.
 *
 * @param value - The raw string value from VITE_E2E_AUTH_INJECTION
 * @returns true only when value is exactly "true"
 */
export function isE2EAuthInjectionFlagEnabled(value: unknown): boolean {
  // Security: Only exact string "true" enables the seam
  // All other values (false, undefined, empty, "1", "yes", etc.) are disabled
  return value === 'true';
}

/**
 * Check if E2E auth injection is enabled.
 *
 * Security contract:
 *   mode=e2e       + flag="true"  → enabled
 *   mode=e2e       + anything else → disabled
 *   mode=production + any flag value → disabled (MODE check is authoritative)
 *
 * Uses static import.meta.env access per Vite's documented build contract.
 * The MODE check is the primary authority preventing production bypass.
 */
export function isE2EAuthInjectionEnabled(): boolean {
  // Primary security gate: only e2e mode allows the seam
  // This prevents production builds from accidentally enabling the seam
  // even if VITE_E2E_AUTH_INJECTION=true is set
  if (import.meta.env.MODE !== 'e2e') {
    return false;
  }

  // Secondary: flag must be explicitly "true"
  return isE2EAuthInjectionFlagEnabled(import.meta.env.VITE_E2E_AUTH_INJECTION);
}

/**
 * Attempt to apply E2E auth injection from pre-injected state.
 *
 * Called during auth store initialization to check for pre-injected state.
 * Only applies if the E2E auth injection flag is enabled.
 *
 * @param authStore - The auth store instance
 * @param storage - Storage adapter for persistence (defaults to localStorage)
 */
export const E2E_AUTH_DISABLED_KEY = '__INDEEP_E2E_AUTH_DISABLED__';

/**
 * Returns true if a window-state E2E auth injection is currently
 * present in the page (i.e. window.__INDEEP_E2E_AUTH_STATE__ has
 * been set by a Playwright addInitScript). This is the source-of-truth
 * check for "should the bootstrap re-validate the session via
 * getSession(), or trust the injected state as canonical?"
 *
 * E2E suites that DO inject the window state (e.g.
 * profile-completion-window-state.spec.ts) can skip the
 * getSession() call to avoid the round-trip. E2E suites that do
 * NOT inject the window state (e.g. the b2b-join profile-media
 * suite, which uses a real session cookie) must re-validate on
 * every mount to avoid stale persisted authStatus without a
 * matching accountId.
 */
export function isE2EWindowStateInjected(): boolean {
  if (typeof window === 'undefined') return false;
  const injected = (window as WindowWithE2EAuth).__INDEEP_E2E_AUTH_STATE__;
  return Boolean(injected && injected.authenticated);
}

export function applyE2EAuthInjection(
  authStore: { setState: (state: Partial<AuthState>) => void; getState: () => AuthState },
  storage: Storage = localStorage,
): void {
  if (!isE2EAuthInjectionEnabled()) {
    return;
  }

  // Check if E2E auth has been disabled for this session (e.g., after logout)
  // This marker is set by disableE2EAuthReinjection() in auth-helper.ts
  if (storage.getItem(E2E_AUTH_DISABLED_KEY) === 'true') {
    return;
  }

  const injected = window.__INDEEP_E2E_AUTH_STATE__;
  if (!injected) {
    return;
  }

  // Derive sponsor access state from the injected user data
  // Matches the logic in hydrateFromSession from authStore.ts
  const hasSponsorAccess = injected.user.has_sponsor_access ?? false;
  const sponsorAccessState = hasSponsorAccess ? 'manual_access' : 'unknown';
  const sponsorTier: AuthState['sponsorTier'] = hasSponsorAccess ? 'active' : 'none';
  const isAdmin = injected.user.is_admin ?? false;

  // CORRECTION04 (B-2.B): the E2E injection contract now sets
  // accountId too, so downstream consumers that require a canonical
  // account id (notably ProfileQuestionnairePage's C1 gate) have
  // it from the injection alone — regardless of whether the auth
  // bootstrap short-circuits on this mount. The short-circuit in
  // useAuthBootstrap now requires BOTH the E2E flag AND an injected
  // window-state to skip getSession(); suites that inject the
  // window-state provide the accountId via injection.
  authStore.setState({
    isAuthenticated: true,
    sessionId: `e2e-session-${injected.user.id}`,
    authStatus: 'authenticated',
    accountId: injected.user.id,
    registrationKind: injected.user.registration_kind as AuthState['registrationKind'],
    sponsorAccessState: sponsorAccessState as AuthState['sponsorAccessState'],
    hasSponsorAccess,
    sponsorTier,
    isAdmin,
  });

  // Store E2E session marker
  storage.setItem(E2E_SESSION_KEY, JSON.stringify({
    userId: injected.user.id,
    injectedAt: Date.now(),
  }));

  // Mark that E2E injection was used (for test verification)
  storage.setItem(E2E_AUTH_DISABLED_KEY, 'false');
}

/**
 * Get E2E auth injection metadata from storage.
 *
 * @param storage - Storage adapter (defaults to localStorage)
 * @returns E2E session metadata or null
 */
export function getE2EAuthInjectionMetadata(
  storage: Storage = localStorage,
): { userId: string; injectedAt: number } | null {
  const raw = storage.getItem(E2E_SESSION_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as { userId: string; injectedAt: number };
  } catch {
    return null;
  }
}

/**
 * Check if E2E auth injection is currently active.
 *
 * @param storage - Storage adapter (defaults to localStorage)
 * @returns true if E2E injection was used
 */
export function isE2EAuthInjectionActive(
  storage: Storage = localStorage,
): boolean {
  const meta = getE2EAuthInjectionMetadata(storage);
  return meta !== null;
}

/**
 * Clear E2E auth injection state.
 *
 * @param storage - Storage adapter (defaults to localStorage)
 */
export function clearE2EAuthInjection(
  storage: Storage = localStorage,
): void {
  storage.removeItem(E2E_SESSION_KEY);
  storage.removeItem(E2E_AUTH_DISABLED_KEY);
}

/**
 * Initialize E2E auth injection.
 *
 * Called from main.tsx when in e2e mode. Checks for pre-injected state
 * and applies it if the E2E auth injection flag is enabled.
 *
 * Uses static import of useAuthStore for proper ES module resolution.
 */
export function initializeE2EAuthInjection(): void {
  if (!isE2EAuthInjectionEnabled()) {
    return;
  }
  // Pass the store itself (has getState/setState methods), not the state object
  applyE2EAuthInjection(useAuthStore);
}
